/* ==========================================================================
   Quantity Limit Enforcer — Max 10 units per item
   -----------------------------------------------------------------------
   PART A — Product page: PROACTIVE prevention
     Before the customer can even submit "Add to Cart", we check how many
     units of this variant are already sitting in the cart, and shrink the
     quantity input's own max accordingly. This stops the "cart already has
     10, customer adds 1 more from product page = 11" bug before it happens.

   PART B — Everywhere: toast message near the quantity control
     Same "Buy in Bulk" message as before, shown whenever a limit is hit.

   PART C — Safety net: dynamic correction (no page reload)
     Watches every cart-changing request (fetch or XHR) and, after each one,
     re-checks the real cart. If any line is still over the limit, it's
     corrected via the Cart AJAX API and the cart UI is updated in place
     using Shopify's Sections Rendering API — no full page reload.
     The toast is now anchored to the specific cart line's quantity input
     that was corrected, instead of a generic page anchor.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  const MAX_QTY = 10;
  const CONTACT_PAGE_URL = '/pages/contact-us';
  const DISPLAY_DURATION = 6000; // ms

  /* ------------------------------------------------------------------ */
  /* Toast (message box)                                                 */
  /* ------------------------------------------------------------------ */
  let toastEl = null;
  let hideTimeout = null;
  let repositionTarget = null;

  function buildToast() {
    const toast = document.createElement('div');
    toast.className = 'bulk-limit-toast';
    toast.innerHTML = `
      <div class="bulk-limit-toast__icon">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.5 7.5v9a2 2 0 0 1-1 1.73l-7 4a2 2 0 0 1-2 0l-7-4a2 2 0 0 1-1-1.73v-9a2 2 0 0 1 1-1.73l7-4a2 2 0 0 1 2 0l7 4a2 2 0 0 1 1 1.73Z"/>
          <path d="M3.3 7 12 12l8.7-5"/>
          <path d="M12 22V12"/>
        </svg>
      </div>
      <div class="bulk-limit-toast__text">
        <strong>Buy in Bulk</strong>
        <p><span class="bulk-limit-toast__msg">Looking to make bulk orders?</span> <a href="${CONTACT_PAGE_URL}">Inquire Now →</a></p>
      </div>
      <button type="button" class="bulk-limit-toast__close" aria-label="Close">&times;</button>
    `;
    document.body.appendChild(toast);
    toast.querySelector('.bulk-limit-toast__close').addEventListener('click', hideToast);
    return toast;
  }

  function getToast() {
    if (!toastEl) toastEl = buildToast();
    return toastEl;
  }

  function positionToast(toast, anchor) {
    const rect = anchor.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0 && rect.top === 0 && rect.left === 0) return;

    const toastWidth = toast.offsetWidth || 300;
    const toastHeight = toast.offsetHeight || 80;
    const gap = 10;

    let left = rect.left;
    let top = rect.bottom + gap;

    const maxLeft = window.innerWidth - toastWidth - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    if (left < 12) left = 12;

    if (top + toastHeight > window.innerHeight - 12) {
      top = rect.top - toastHeight - gap;
    }

    toast.style.left = left + 'px';
    toast.style.top = top + 'px';
  }

  function trackReposition(anchor) {
    repositionTarget = anchor;
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
  }

  function stopTrackingReposition() {
    repositionTarget = null;
    window.removeEventListener('scroll', handleReposition, true);
    window.removeEventListener('resize', handleReposition);
  }

  function handleReposition() {
    if (toastEl && repositionTarget) positionToast(toastEl, repositionTarget);
  }

  function showToast(anchor, message) {
    const toast = getToast();
    clearTimeout(hideTimeout);

    if (message) toast.querySelector('.bulk-limit-toast__msg').textContent = message;

    positionToast(toast, anchor);
    requestAnimationFrame(() => {
      positionToast(toast, anchor);
      toast.classList.add('is-visible');
    });

    trackReposition(anchor);
    hideTimeout = setTimeout(hideToast, DISPLAY_DURATION);
  }

  function hideToast() {
    if (!toastEl) return;
    toastEl.classList.remove('is-visible');
    stopTrackingReposition();
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */
  function isCartLineInput(input) {
    return !!input.closest('#main-cart-items') || !!input.closest('cart-items');
  }

  // Cart-line inputs always use the flat MAX_QTY. Product-page inputs use
  // a per-variant "remaining allowance" computed in Part A below.
  function getEffectiveMax(input) {
    if (isCartLineInput(input)) return MAX_QTY;
    const stored = input.dataset.effectiveMax;
    return stored !== undefined ? parseInt(stored, 10) : MAX_QTY;
  }

  function enforceLimit(input) {
    if (!input) return;
    const val = parseInt(input.value, 10);
    if (isNaN(val)) return;

    const effectiveMax = getEffectiveMax(input);
    if (val > effectiveMax) {
      input.value = effectiveMax;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      showToast(input, buildMessage(input, effectiveMax));
    }
  }

  function buildMessage(input, effectiveMax) {
    if (isCartLineInput(input) || effectiveMax === MAX_QTY) {
      return 'Looking to make bulk orders?';
    }
    const already = MAX_QTY - effectiveMax;
    if (effectiveMax <= 0) {
      return `You already have ${already} in your cart (max ${MAX_QTY} per item).`;
    }
    return `You already have ${already} in your cart — max ${effectiveMax} more allowed.`;
  }

  // Finds the actual quantity input for a given variant on the cart page,
  // so the toast can anchor to the exact row that was corrected instead
  // of a generic page landmark.
  function findCartLineInput(variantId) {
    const inputs = document.querySelectorAll(
      '#main-cart-items input[type="number"].quantity__input, cart-items input[type="number"].quantity__input'
    );
    for (const inp of inputs) {
      if (String(inp.dataset.quantityVariantId) === String(variantId)) return inp;
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* PART A — Product page: proactive max based on existing cart qty     */
  /* ------------------------------------------------------------------ */
  function refreshProductPageLimits() {
    const inputs = document.querySelectorAll('input[type="number"].quantity__input');
    inputs.forEach(function (input) {
      if (isCartLineInput(input)) return; // cart page handles itself

      const variantId = input.dataset.quantityVariantId;
      if (!variantId) return;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          const existingQty = cart.items
            .filter(function (item) { return String(item.variant_id) === String(variantId); })
            .reduce(function (sum, item) { return sum + item.quantity; }, 0);

          const allowed = Math.max(0, MAX_QTY - existingQty);

          input.dataset.effectiveMax = allowed;
          input.max = allowed;

          // If nothing more can be added, cap the input and warn the
          // customer without waiting for them to try first.
          if (allowed <= 0) {
            input.value = 0;
          } else if (parseInt(input.value, 10) > allowed) {
            input.value = allowed;
          }

          toggleAddToCartButton(input, allowed);
        });
    });
  }

  function toggleAddToCartButton(input, allowed) {
    const form = input.closest('form');
    if (!form) return;
    const submitBtn = form.querySelector('[type="submit"], [name="add"]');
    if (!submitBtn) return;

    if (allowed <= 0) {
      submitBtn.dataset.bulkLimitDisabled = 'true';
    } else {
      delete submitBtn.dataset.bulkLimitDisabled;
    }
  }

  // Recalculate whenever the customer changes a variant option (color,
  // size, etc.) — give the theme's own variant-swap JS a moment to finish
  // updating the DOM first.
  document.addEventListener('change', function (e) {
    if (e.target.closest('form') && e.target.closest('form').querySelector('input.quantity__input')) {
      setTimeout(refreshProductPageLimits, 350);
    }
  });

  refreshProductPageLimits();

  /* ------------------------------------------------------------------ */
  /* PART B — Event listeners using the effective (dynamic) max          */
  /* ------------------------------------------------------------------ */
  document.addEventListener('input', function (e) {
    if (e.target.matches('input[type="number"].quantity__input')) enforceLimit(e.target);
  });

  document.addEventListener('change', function (e) {
    if (e.target.matches('input[type="number"].quantity__input')) enforceLimit(e.target);
  });

  document.addEventListener(
    'click',
    function (e) {
      const plusBtn = e.target.closest('.quantity__button[name="plus"]');
      if (!plusBtn) return;

      const wrapper = plusBtn.closest('quantity-input');
      if (!wrapper) return;
      const input = wrapper.querySelector('input.quantity__input');
      if (!input) return;

      const val = parseInt(input.value, 10);
      const effectiveMax = getEffectiveMax(input);
      if (!isNaN(val) && val >= effectiveMax) {
        showToast(input, buildMessage(input, effectiveMax));
      }
    },
    true
  );

  // Block "Add to Cart" entirely if this variant is already at (or over)
  // its allowance — stops the request from ever being sent.
  document.addEventListener(
    'submit',
    function (e) {
      const qtyInput = e.target.querySelector('input[type="number"].quantity__input');
      if (!qtyInput) return;

      const val = parseInt(qtyInput.value, 10);
      const effectiveMax = getEffectiveMax(qtyInput);

      if (!isNaN(val) && (val > effectiveMax || effectiveMax <= 0)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showToast(qtyInput, buildMessage(qtyInput, effectiveMax));
      }
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* PART C — Safety net: watch cart-changing requests, correct in place */
  /* ------------------------------------------------------------------ */
  function getCartSectionId() {
    const container = document.querySelector('#main-cart-items');
    return container ? container.dataset.id : null;
  }

  function updateCartSectionDOM(html) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(html, 'text/html');
    const newContainer = newDoc.querySelector('#main-cart-items');
    const oldContainer = document.querySelector('#main-cart-items');
    if (newContainer && oldContainer) {
      oldContainer.innerHTML = newContainer.innerHTML;
    }
  }

  function updateCartCountBubble(itemCount) {
    document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
      const numEl = el.querySelector('span') || el;
      numEl.textContent = itemCount;
    });
  }

  function reconcileCartQuantities() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const overLimitItems = cart.items.filter(function (item) { return item.quantity > MAX_QTY; });
        if (overLimitItems.length === 0) {
          refreshProductPageLimits();
          return;
        }

        const sectionId = getCartSectionId();
        const correctionChain = overLimitItems.reduce(function (chain, item) {
          return chain.then(function () {
            const body = { id: item.key, quantity: MAX_QTY };
            if (sectionId) {
              body.sections = sectionId;
              body.sections_url = window.location.pathname;
            }
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            }).then(function (r) { return r.json(); });
          });
        }, Promise.resolve());

        correctionChain.then(function (lastResponse) {
          if (lastResponse) {
            if (sectionId && lastResponse.sections && lastResponse.sections[sectionId]) {
              updateCartSectionDOM(lastResponse.sections[sectionId]);
            }
            if (typeof lastResponse.item_count !== 'undefined') {
              updateCartCountBubble(lastResponse.item_count);
            }
          }

          // Anchor the toast to the actual corrected line's quantity
          // input (now re-rendered in the DOM), falling back to a
          // sensible default only if that row can't be found.
          const firstOverLimit = overLimitItems[0];
          const lineAnchor =
            findCartLineInput(firstOverLimit.variant_id) ||
            document.querySelector('.title-wrapper-with-link') ||
            document.body;

          showToast(lineAnchor, `Quantity limited to ${MAX_QTY} per item.`);
          refreshProductPageLimits();
        });
      });
  }

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    const isCartWrite =
      requestUrl.indexOf('/cart/add') !== -1 ||
      requestUrl.indexOf('/cart/change') !== -1 ||
      requestUrl.indexOf('/cart/update') !== -1;

    return originalFetch.apply(this, args).then(function (response) {
      if (isCartWrite && response.ok) {
        setTimeout(reconcileCartQuantities, 350);
      }
      return response;
    });
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', function () {
      const isCartWrite =
        typeof url === 'string' &&
        (url.indexOf('/cart/add') !== -1 ||
          url.indexOf('/cart/change') !== -1 ||
          url.indexOf('/cart/update') !== -1);
      if (isCartWrite && this.status >= 200 && this.status < 300) {
        setTimeout(reconcileCartQuantities, 350);
      }
    });
    return originalXhrOpen.apply(this, arguments);
  };

  // Catches plain (non-AJAX) form submissions too, since this runs fresh
  // on every page load regardless of how the previous add happened.
  reconcileCartQuantities();
});